// The checkout a visitor chose before signing in, and the two requests that
// carry it out once they have. Shared by startCheckout (pricing and exam
// pages), Login and continueAfterSignIn (Google, LinkedIn and magic-link
// callbacks), so the body, the "already own it" test and the echo check are
// written once.
//
// A pending checkout is {tier, exam, package, subject}. It lives in
// sessionStorage (and, for the sign-in redirect, the login URL). There is no
// default exam: a pending checkout with no exam returns the visitor to
// /pricing to choose one.
import { getBaseApiUrl } from '../../config/api';
import {
  ORG_MEMBER_FALLBACK_MESSAGE,
  checkoutRefusalMessage,
  clearPendingCheckout,
  showCheckoutRefusal,
} from './checkoutRefusal';
import { userMayPurchase } from './purchaseEligibility';

export const PENDING_KEYS = {
  tier: 'pending_checkout_tier',
  exam: 'pending_checkout_exam',
  package: 'pending_checkout_package',
  subject: 'pending_checkout_subject',
};

export const CHECKOUT_MISMATCH_MESSAGE =
  'We could not confirm your selection, so nothing was charged. Please try again from the pricing page.';

const TOKEN_KEYS = [
  ['localStorage', 'pathbuddy_access_token'],
  ['sessionStorage', 'pathbuddy_access_token'],
  ['localStorage', 'token'],
];

const TIER_RANK = { beginnings: 1, foundations: 2, beyond: 3 };

export function readAuthToken() {
  for (let i = 0; i < TOKEN_KEYS.length; i += 1) {
    const storeName = TOKEN_KEYS[i][0];
    const key = TOKEN_KEYS[i][1];
    try {
      const value = window[storeName].getItem(key);
      if (value) return value;
    } catch (err) {
      // Storage can be unavailable (private mode, blocked site data).
    }
  }
  return null;
}

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The tier a stored value names: 'beyond', 'foundations', 'beginnings',
 * 'free' (the landing pages' free link) or null. Accepts a plan key
 * (`lnat_beyond`) or a bare tier.
 *
 * @param {unknown} tier
 * @returns {'beyond'|'foundations'|'beginnings'|'free'|null}
 */
export function tierSlugOf(tier) {
  const text = clean(tier).toLowerCase();
  if (text.includes('beyond')) return 'beyond';
  if (text.includes('foundation')) return 'foundations';
  if (text.includes('beginning')) return 'beginnings';
  if (text.includes('free')) return 'free';
  return null;
}

function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (err) {
    return null;
  }
}

/**
 * The pending checkout, from the login URL first and sessionStorage second.
 * Every field is a trimmed string, empty when absent.
 *
 * @param {string} [search] a query string, default the page's own
 * @returns {{tier: string, exam: string, package: string, subject: string}}
 */
export function readPendingCheckout(search) {
  const params = new URLSearchParams(typeof search === 'string' ? search : window.location.search);
  // A pending checkout is one unit: a URL that names a tier or exam is a
  // fresh choice, so nothing left in storage by an earlier one is mixed in.
  const fromUrl = Boolean(clean(params.get('tier')) || clean(params.get('exam')));
  const pick = (name, key) => clean(params.get(name)) || (fromUrl ? '' : clean(readSession(key)));
  return {
    tier: pick('tier', PENDING_KEYS.tier),
    exam: pick('exam', PENDING_KEYS.exam).toLowerCase(),
    package: pick('package', PENDING_KEYS.package).toLowerCase(),
    subject: pick('subject', PENDING_KEYS.subject),
  };
}

/** Stores the pending checkout, dropping any package or subject left by an earlier one. */
export function rememberPendingCheckout({ tierKey, examId, packageKey, subject }) {
  try {
    sessionStorage.setItem(PENDING_KEYS.tier, tierKey);
    sessionStorage.setItem(PENDING_KEYS.exam, examId);
    [['package', packageKey], ['subject', subject]].forEach(([name, value]) => {
      if (value) sessionStorage.setItem(PENDING_KEYS[name], value);
      else sessionStorage.removeItem(PENDING_KEYS[name]);
    });
  } catch (err) {
    // Storage can be unavailable. The login URL still carries the choice.
  }
}

/**
 * Copies the choice in the login URL into sessionStorage before an SSO
 * redirect, which leaves the page. A URL naming a tier or exam is a whole
 * choice: any key it lacks (package, subject) is removed, not kept from an
 * earlier choice. A URL naming neither leaves storage as it was.
 */
export function storePendingCheckoutFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = Boolean(clean(params.get('tier')) || clean(params.get('exam')));
  Object.keys(PENDING_KEYS).forEach((name) => {
    const value = clean(params.get(name));
    if (!value && !fromUrl) return;
    try {
      if (value) sessionStorage.setItem(PENDING_KEYS[name], value);
      else sessionStorage.removeItem(PENDING_KEYS[name]);
    } catch (err) {
      // Storage can be unavailable; the redirect proceeds without it.
    }
  });
}

/**
 * Whether the user's `access.owned` already holds `exam` at `tierSlug` or
 * above. A missing or malformed `owned` (a user stored before it existed)
 * owns nothing. An all-tracks account owns everything.
 *
 * @param {object|null|undefined} user
 * @param {string} exam
 * @param {string|null} tierSlug
 * @returns {boolean}
 */
export function ownsExamAtTier(user, exam, tierSlug) {
  const owned = user && user.access && user.access.owned;
  if (!owned || typeof owned !== 'object') return false;
  if (owned.all_tracks === true) return true;
  const wanted = TIER_RANK[tierSlug];
  if (!wanted || !Array.isArray(owned.exams)) return false;
  return owned.exams.some(
    (held) => held && held.exam === exam && (TIER_RANK[held.tier] || 0) >= wanted
  );
}

function postJson(path, payload) {
  const headers = { 'Content-Type': 'application/json' };
  const token = readAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${getBaseApiUrl()}${path}`, { method: 'POST', headers, body: JSON.stringify(payload) });
}

function sameText(a, b) {
  return clean(a).toLowerCase() === clean(b).toLowerCase();
}

/**
 * Whether the create-session response is for what was asked. A backend that
 * ignored the package (an old one) echoes no package, which is a mismatch
 * when one was requested. A missing echo of `package_key` or `subject` is
 * "none", which matches a request that named none.
 */
export function echoMatchesRequest(data, { tier, exam, packageKey, subject }) {
  if (!data || typeof data !== 'object') return false;
  const planKey = data.plan_key || data.planKey;
  if (planKey && !sameText(planKey, `${exam}_${tier}`)) return false;
  const echoedPackage = clean(data.package_key).toLowerCase();
  if (packageKey) {
    if (!echoedPackage.startsWith(`interview_${packageKey.toLowerCase()}`)) return false;
  } else if (echoedPackage) {
    return false;
  }
  return sameText(data.subject, subject || '');
}

/**
 * POST /api/stripe/create-checkout-session with {exam, tier, package, subject,
 * promo_code?}.
 *
 * Resolves to one of:
 *  - {status: 'redirect', url}: the session is for what was asked
 *  - {status: 'refused', message}: the server refused; nothing was charged
 *  - {status: 'mismatch'}: the echo differs from the request; do not redirect
 *  - {status: 'failed'}: no usable answer (network error, no URL)
 */
export async function requestCheckoutSession({ tier, exam, packageKey, subject, promoCode }) {
  try {
    const res = await postJson('/api/stripe/create-checkout-session', {
      exam,
      tier,
      ...(packageKey ? { package: packageKey } : {}),
      ...(subject ? { subject } : {}),
      ...(promoCode ? { promo_code: promoCode } : {}),
    });
    const data = await res.json().catch(() => ({}));
    const refusal = checkoutRefusalMessage(res, data);
    if (refusal) return { status: 'refused', message: refusal };
    const url = data && (data.checkoutUrl || data.url);
    if (!url) return { status: 'failed' };
    if (!echoMatchesRequest(data, { tier, exam, packageKey, subject })) return { status: 'mismatch' };
    return { status: 'redirect', url };
  } catch (err) {
    return { status: 'failed' };
  }
}

/**
 * POST /api/purchases/beginnings (B5): the free Beginnings choice of an exam
 * and optionally a package. Resolves to {status: 'granted'},
 * {status: 'refused', message} or {status: 'failed'}.
 */
export async function requestBeginnings({ exam, packageKey, subject }) {
  try {
    const res = await postJson('/api/purchases/beginnings', {
      ...(exam ? { exam } : {}),
      ...(packageKey ? { package: packageKey } : {}),
      ...(subject ? { subject } : {}),
    });
    const data = await res.json().catch(() => ({}));
    const refusal = checkoutRefusalMessage(res, data);
    if (refusal) return { status: 'refused', message: refusal };
    if (res.status === 200 || res.status === 201) return { status: 'granted' };
    return { status: 'failed' };
  } catch (err) {
    return { status: 'failed' };
  }
}

/**
 * Carries out the pending checkout after sign-in. Clears the pending keys
 * once it acts on them. Resolves to {action, ...}:
 *  - 'none': nothing to do (no pending paid or Beginnings tier, or an
 *    organisation member with a Beginnings choice, left as it was)
 *  - 'pricing': a checkout with no exam; go back to /pricing
 *  - 'practice' (+exam): the exam is already owned, or Beginnings was granted
 *  - 'redirect' (+url): go to Stripe
 *  - 'refused': the user was told why (alert); carry on to the normal route
 *  - 'no_url': the server gave nothing to follow
 */
export async function runPendingCheckout(user, search) {
  const pending = readPendingCheckout(search);
  const slug = tierSlugOf(pending.tier);
  if (slug !== 'beyond' && slug !== 'foundations' && slug !== 'beginnings') return { action: 'none' };
  const paid = slug !== 'beginnings';
  if (!userMayPurchase(user)) {
    if (!paid) return { action: 'none' };
    showCheckoutRefusal(ORG_MEMBER_FALLBACK_MESSAGE);
    return { action: 'refused' };
  }
  if (!pending.exam) {
    clearPendingCheckout();
    return { action: 'pricing' };
  }
  if (paid && ownsExamAtTier(user, pending.exam, slug)) {
    clearPendingCheckout();
    return { action: 'practice', exam: pending.exam };
  }
  clearPendingCheckout();
  try {
    sessionStorage.removeItem('needs_onboarding');
  } catch (err) {
    // Storage can be unavailable; there is nothing to remove.
  }
  const choice = { exam: pending.exam, packageKey: pending.package, subject: pending.subject };
  if (!paid) {
    const result = await requestBeginnings(choice);
    if (result.status === 'granted') return { action: 'practice', exam: pending.exam };
    if (result.status === 'refused') {
      showCheckoutRefusal(result.message);
      return { action: 'refused' };
    }
    return { action: 'no_url' };
  }
  const result = await requestCheckoutSession({ ...choice, tier: slug });
  if (result.status === 'redirect') return { action: 'redirect', url: result.url };
  if (result.status === 'refused') {
    showCheckoutRefusal(result.message);
    return { action: 'refused' };
  }
  if (result.status === 'mismatch') {
    showCheckoutRefusal(CHECKOUT_MISMATCH_MESSAGE);
    return { action: 'refused' };
  }
  return { action: 'no_url' };
}
